import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from 'app/config';

// What a change of the bg-check method (the paper choice, its discard) can alter on the holder application. The server
// answers both with the updated holder DTO; exactly these fields are taken from it into the cached application. Not
// the whole DTO: what the holder is still typing lives in the wizard's forms and only reaches the server through the
// debounced autosave, so replacing the rest (`cardHolder`, ...) under them with the server's older copy would be wrong.
const BG_CHECK_STATE_FIELDS = [
  'bgCheck',
  'isCardHolderBgCheckSigned',
  'isCardHolderPaperBgCheck',
  'isBgCheckOnFile',
  // The paper choice doubles the link's validity (and leaving it halves it again).
  'linkExpiresAt',
];

const isHolderApplication = (data) => !!data && typeof data === 'object' && !Array.isArray(data);

const takeBgCheckState = (draft, serverApplication) => {
  BG_CHECK_STATE_FIELDS.forEach((field) => {
    if (field in serverApplication) {
      draft[field] = serverApplication[field];
    }
  });
};

export const publicLubaApi = createApi({
  reducerPath: 'publicLubaApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config.API_BASE_URL,
  }),
  endpoints: (builder) => ({
    getApplicationByIdAndControlCode: builder.query({
      query({ applicationId, controlCode }) {
        return `/cardholder/${applicationId}/${controlCode}`;
      },
    }),
    getClassifiers: builder.query({
      query: () => `/classificator/list`,
      keepUnusedDataFor: 60 * 60 * 24 * 7, // keep for a week
    }),
    getClassifierValues: builder.query({
      query: (classifierId) => `/classificator/values/${classifierId}`,
      keepUnusedDataFor: 60 * 60 * 24, // keep for a day
    }),

    uploadPhotoByHolder: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/photo`,
          method: 'POST',
          body,
        };
      },
    }),
    updateApplication: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareApplicationIdCard: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/signprepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signApplicationWithIdCard: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/sign`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareApplicationSmartId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/smartid_prepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signApplicationWithSmartId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/smartid_sign`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareApplicationMobileId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/mobileid_prepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signApplicationWithMobileId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/mobileid_sign`,
          method: 'POST',
          body,
        };
      },
    }),
    validateApplication: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/application/validate`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareBackgroundCheckIdCard: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/signprepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signBackgroundCheckWithIdCard: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/sign`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareBackgroundCheckSmartId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/smartid_prepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signBackgroundCheckWithSmartId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/smartid_sign`,
          method: 'POST',
          body,
        };
      },
    }),
    signPrepareBackgroundCheckMobileId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/mobileid_prepare`,
          method: 'POST',
          body,
        };
      },
    }),
    signBackgroundCheckWithMobileId: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/mobileid_sign`,
          method: 'POST',
          body,
        };
      },
    }),
    validateBackgroundCheck: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/validate`,
          method: 'POST',
          body,
        };
      },
    }),
    uploadBackgroundCheck: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgcheck/upload`,
          method: 'POST',
          body,
        };
      },
      // The holder application is fetched once per session and must NOT be refetched (see `deleteBackgroundCheck`):
      // what the upload did on the server is put into the cached copy from its response (`{ id, name, url }`) - the
      // file is the application's bg-check now, and it replaces the web-signed container and a paper choice, which
      // the server drops with it. (The link's validity may shrink back with the paper choice; the upload response does
      // not say, and nothing here reads it until the next load.)
      async onQueryStarted({ applicationId, controlCode }, { dispatch, queryFulfilled }) {
        let response;
        try {
          response = await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          publicLubaApi.util.updateQueryData('getApplicationByIdAndControlCode', { applicationId, controlCode }, (draft) => {
            if (!draft) {
              return;
            }
            draft.bgCheck = {
              applicantSignDate: draft.bgCheck?.applicantSignDate ?? null,
              url: response?.data?.url ?? `cardholder/${applicationId}/${controlCode}/bgCheck`,
              cardHolderSignDate: null,
            };
            draft.isCardHolderBgCheckSigned = false;
            draft.isCardHolderPaperBgCheck = false;
          })
        );
      },
    }),
    // The holder brings the bg-check form on paper (they still sign the application digitally): answers with the
    // updated holder application, `isCardHolderPaperBgCheck: true`. Idempotent. 400 `logic_exception`:
    // `application.error.bgcheck_method_locked` (the application is signed), `application.error.bgcheck_other_method_in_use`
    // (an uploaded or web-signed bg-check exists: discard it first, `deleteBackgroundCheck`),
    // `application.error.bgcheck_not_required` (a secondary application), 404 for a wrong control code.
    setPaperBackgroundCheck: builder.mutation({
      query({ applicationId, controlCode }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgCheck/paper`,
          method: 'PUT',
        };
      },
      // The holder application is fetched once per session and must NOT be refetched (see `deleteBackgroundCheck`):
      // take what the choice changed (the flag, the link's new end, ...) from the response into the cached copy, so
      // every reader sees it at once.
      async onQueryStarted({ applicationId, controlCode }, { dispatch, queryFulfilled }) {
        let response;
        try {
          response = await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          publicLubaApi.util.updateQueryData('getApplicationByIdAndControlCode', { applicationId, controlCode }, (draft) => {
            if (draft) {
              if (isHolderApplication(response?.data)) {
                takeBgCheckState(draft, response.data);
              }
              // A 2xx is the choice made, even from a body that does not repeat the flag.
              if (!isHolderApplication(response?.data) || !('isCardHolderPaperBgCheck' in response.data)) {
                draft.isCardHolderPaperBgCheck = true;
              }
            }
          })
        );
      },
    }),
    // Discards the holder's bg-check file, web-signed state and paper choice (changing the submission method). 200 with
    // the updated holder application (also when there was nothing to delete; an older backend answered with an empty
    // body); 400 `logic_exception` for a rejected link, a locked method (`application.error.bgcheck_method_locked`) or
    // a paper choice the link depends on (`application.error.bgcheck_paper_window_locked`), 404 for a wrong control
    // code. The body is read as text first: the default JSON handler would choke on an empty success body, and a plain
    // 'text' handler would leave an error body unparsed.
    deleteBackgroundCheck: builder.mutation({
      query({ applicationId, controlCode }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/bgCheck`,
          method: 'DELETE',
          responseHandler: async (response) => {
            const text = await response.text();
            if (!text) {
              return null;
            }
            try {
              return JSON.parse(text);
            } catch {
              return text;
            }
          },
        };
      },
      // The holder application is fetched once per session and must NOT be refetched (a refetch flips `isFetching`,
      // which unmounts the whole wizard): patch the cached copy from the updated application the server answered with,
      // so every reader sees the file, the web-signed flag and the holder's paper choice gone at once and the link's
      // validity back at its regular end.
      async onQueryStarted({ applicationId, controlCode }, { dispatch, queryFulfilled }) {
        let response;
        try {
          response = await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          publicLubaApi.util.updateQueryData('getApplicationByIdAndControlCode', { applicationId, controlCode }, (draft) => {
            if (!draft) {
              return;
            }
            if (isHolderApplication(response?.data)) {
              takeBgCheckState(draft, response.data);
            } else {
              // An older backend answered with nothing: what a discard always does.
              draft.bgCheck = null;
              draft.isCardHolderBgCheckSigned = false;
              draft.isCardHolderPaperBgCheck = false;
            }
          })
        );
      },
    }),
    submitCardHolderApplication: builder.mutation({
      query({ applicationId, controlCode, body }) {
        return {
          url: `/cardholder/${applicationId}/${controlCode}/submit`,
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

export const {
  useGetApplicationByIdAndControlCodeQuery,
  useUploadPhotoByHolderMutation,
  useUpdateApplicationMutation,
  useUploadBackgroundCheckMutation,
  useDeleteBackgroundCheckMutation,
  useSetPaperBackgroundCheckMutation,
  useSubmitCardHolderApplicationMutation,
  useValidateApplicationMutation,
  useValidateBackgroundCheckMutation,

  // application signing
  useSignPrepareApplicationIdCardMutation,
  useSignApplicationWithIdCardMutation,
  useSignPrepareApplicationSmartIdMutation,
  useSignApplicationWithSmartIdMutation,
  useSignPrepareApplicationMobileIdMutation,
  useSignApplicationWithMobileIdMutation,

// bgcheck signing
  useSignPrepareBackgroundCheckIdCardMutation,
  useSignBackgroundCheckWithIdCardMutation,
  useSignPrepareBackgroundCheckSmartIdMutation,
  useSignBackgroundCheckWithSmartIdMutation,
  useSignPrepareBackgroundCheckMobileIdMutation,
  useSignBackgroundCheckWithMobileIdMutation,

  useGetClassifierValuesQuery,
  useGetClassifiersQuery,
} = publicLubaApi;
